<template>
  <div class="max-w-7xl mx-auto pb-10 relative">
    <div class="space-y-6 transition-all duration-300 animate-fade-in" :class="{ 'blur-sm pointer-events-none opacity-50': showStatementModal }">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Estado de Cuentas</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm font-medium">Consulta del historial detallado y movimientos de cartera.</p>
        </div>
      </div>

      <div class="bg-white dark:bg-slate-900 p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none">
        <div class="relative group">
          <Search class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 group-focus-within:text-brand-500 transition-colors" />
          <input v-model="searchTerm" type="text" @keyup.enter="handleSearch" placeholder="Nombre, Cédula o No. de Préstamo..." class="w-full pl-12 pr-32 py-4 bg-slate-50 dark:bg-slate-800 border-2 border-transparent focus:border-brand-500 rounded-2xl text-base font-bold outline-none transition-all shadow-inner dark:text-white">
          <button @click="handleSearch" :disabled="isLoading" class="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white font-black text-xs uppercase tracking-widest rounded-xl shadow-lg transition-all active:scale-95 disabled:opacity-50">
            <Loader2 v-if="isLoading" class="w-4 h-4 animate-spin" />
            <span v-else>Consultar</span>
          </button>
        </div>
      </div>

      <div class="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-[10px] text-slate-400 uppercase font-black bg-slate-50 dark:bg-slate-800/50 border-b dark:border-slate-800">
              <tr>
                <th class="px-6 py-4">Cliente</th>
                <th class="px-6 py-4">Producto</th>
                <th class="px-6 py-4 text-right">Saldo Actual</th>
                <th class="px-6 py-4 text-center">Estado</th>
                <th class="px-6 py-4 text-right uppercase">Acción</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
              <tr v-for="item in list" :key="item.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group">
                <td class="px-6 py-4">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-slate-500">{{ item.customer_name?.charAt(0) }}</div>
                    <div>
                      <p class="font-black text-slate-900 dark:text-white uppercase leading-none">{{ item.customer_name }}</p>
                      <p class="text-[10px] font-mono text-black mt-1  dark:text-slate-100"> CEDULA:{{ item.customer_doc }}</p>
                      <p class="text-[10px] font-mono text-black mt-1  dark:text-slate-100"> CODIGO:{{ item.customer_code }}</p>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4">
                  <p class="text-xs font-bold text-slate-700 dark:text-slate-300">{{ item.product_name }}</p>
                  <p class="text-[9px] font-black text-brand-600 uppercase  dark:text-slate-100">EXP: {{ item.code }}</p>
                </td>
                <td class="px-6 py-4 text-right">
                  <p class="font-mono font-black text-slate-900 dark:text-white text-base">${{ formatNum(item.remaining_balance) }}</p>
                </td>
                <td class="px-6 py-4 text-center">
                   <span :class="getStatusClass(item.status)" class="px-3 py-1 rounded-lg text-[9px] font-black uppercase">{{ item.status }}</span>
                </td>
                <td class="px-6 py-4 text-right">
                  <button @click="openStatement(item.id)" class="px-5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-[10px] font-black uppercase tracking-widest rounded-xl shadow-sm transition-all active:scale-95 flex items-center gap-2 ml-auto">
                    <BookOpen class="w-4 h-4" /> Ver Historial
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <AccountStatement
      :show="showStatementModal"
      :loanId="selectedLoanId"
      @close="showStatementModal = false"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { Search, Loader2, BookOpen, Wallet } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import { SecureStorage } from '@/utils/secureStorage';
import AccountStatement from './Form.vue';

const list = ref([]);
const isLoading = ref(false);
const searchTerm = ref('');
const showStatementModal = ref(false);
const selectedLoanId = ref(null);
const { showError } = useToast();

const formatNum = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

const handleSearch = async () => {
  if (searchTerm.value.length < 3) return showError('Ingrese al menos 3 caracteres.');
  isLoading.value = true;
  try {
    const user = SecureStorage.getItem('llevo_user');
    const res = await apiClient.get(`/loans?company_id=${user.company}&search=${searchTerm.value}`);
    list.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    showError('Error en la búsqueda.');
  } finally {
    isLoading.value = false;
  }
};

const openStatement = (id) => {
  selectedLoanId.value = id;
  showStatementModal.value = true;
};

const getStatusClass = (status) => {
  if (status === 'ACTIVE') return 'bg-emerald-100 text-emerald-700';
  if (status === 'PAID') return 'bg-blue-100 text-blue-700';
  return 'bg-slate-100 text-slate-600';
};
</script>